import clsx from 'clsx';
import React from 'react';

import styles from './SkeletonLoader.module.scss';

export interface SkeletonLoaderArea {
  /** Class forwarded to the shimmering block, used to size and position it. */
  className?: string;
  /** Inline styles for the shimmering block. Prefer `className` when possible. */
  styles?: React.CSSProperties;
}

export interface SkeletonLoaderProps {
  /** When true, applies a wrapper-with-wrapper style (extra padding/border around the blocks). */
  withWrapper?: boolean;
  /**
   * Custom layout: each entry produces one shimmering block. Each item
   * can specify a `className` and inline `styles` to size and position it.
   * Ignored when `defaultView` is true.
   */
  areas?: SkeletonLoaderArea[];
  /** When true, renders a stock title-plus-content layout and ignores `areas`. */
  defaultView?: boolean;
  /** Extra class forwarded to the wrapping `<section>`. */
  className?: string;
}

/**
 * Renders animated placeholder blocks while content is loading.
 *
 * Pass `areas` for a custom layout (one shimmering block per entry), or
 * set `defaultView` for a built-in title+content placeholder. Wrap with
 * `withWrapper` to add the standard padded/bordered chrome.
 */
export const SkeletonLoader = ({
  areas = [],
  withWrapper,
  defaultView,
  className,
  ...rest
}: SkeletonLoaderProps) => {
  const loadingAreas = defaultView ? (
    <>
      <div className={styles.defaultTitle} data-testid="default-view-title" />
      <div className={styles.defaultContent} />
    </>
  ) : (
    areas.map((area, idx) => (
      <div
        key={idx}
        className={clsx(styles.area, area.className)}
        style={area.styles}
        data-testid="loading-area"
      />
    ))
  );

  const wrapperClassName = clsx(
    styles.wrapper,
    {
      [styles.wrapperWithWrapper]: withWrapper && !defaultView,
      [styles.wrapperDefaultView]: defaultView,
    },
    className
  );

  return (
    <section className={wrapperClassName} {...rest}>
      {loadingAreas}
    </section>
  );
};
